var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
export var MOVE_STEP = 10;
/**
 * Disable any submenu parent whose actionable children are ALL disabled, so
 * keyboard / screen-reader (JAWS, VoiceOver) users can't drill into a submenu
 * where nothing can be activated. Walks depth-first, so an all-disabled nested
 * submenu also disables its own parent.
 *
 * - Leaves a parent untouched if it has no submenu, an empty submenu, or
 *   `customContent` (whose enabled state isn't driven by child `disabled` flags).
 * - `isBack` / `divider` entries are ignored when judging "all disabled".
 * - Preserves an already-`disabled` parent.
 */
export var disableEmptySubmenuParents = function (items) {
    return items.map(function (item) {
        if (!item.submenu || item.submenu.length === 0 || item.customContent) {
            return item;
        }
        var submenu = disableEmptySubmenuParents(item.submenu);
        var actionable = submenu.filter(function (child) { return !child.isBack && !child.divider; });
        var allDisabled = actionable.length > 0 && actionable.every(function (child) { return child.disabled === true; });
        return __assign(__assign({}, item), { submenu: submenu, disabled: item.disabled || allDisabled });
    });
};
// Mirrors TOOL_RULES from GameController — maps manipulative ID prefix to hidden/disabled tools
var CONTEXT_MENU_TOOL_RULES = [
    { match: function (id) { return id.startsWith('linkingCube_'); }, hidden: ['edit', 'group'], disabled: [] },
    { match: function (id) { return id.startsWith('numberChart_'); }, hidden: ['group'], disabled: [] },
    {
        match: function (id) { return id.startsWith('protractor_'); },
        hidden: ['group', 'edit', 'lock'],
        disabled: [],
    },
    { match: function (id) { return id.startsWith('spinner_'); }, hidden: ['group'], disabled: [] },
    { match: function (id) { return id.startsWith('analog_'); }, hidden: ['group'], disabled: [] },
    { match: function (id) { return id.startsWith('timer_'); }, hidden: ['group'], disabled: [] },
    { match: function (id) { return id.startsWith('dice_'); }, hidden: ['group'], disabled: [] },
    { match: function (id) { return id.startsWith('deck_'); }, hidden: ['group'], disabled: [] },
    {
        match: function (id) { return id.startsWith('card_'); },
        hidden: ['group', 'duplicate', 'edit', 'delete'],
        disabled: [],
    },
    {
        match: function (id) { return id.startsWith('shades_'); },
        hidden: ['group', 'edit', 'changeSize'],
        disabled: [],
    },
    {
        match: function (id) { return id.startsWith('annotations_'); },
        hidden: ['group', 'edit'],
        disabled: [],
    },
    {
        match: function (id) { return id.startsWith('text_'); },
        hidden: ['group', 'edit'],
        disabled: [],
    },
    {
        match: function (id) { return id.startsWith('tableText_'); },
        hidden: ['group', 'edit'],
        disabled: ['changeSize'],
    },
    { match: function (id) { return id.startsWith('arrayModel_'); }, hidden: ['group', 'edit'], disabled: [] },
    { match: function (id) { return id.startsWith('numberRacks_'); }, hidden: ['group', 'edit'], disabled: [] },
    { match: function (id) { return id.startsWith('fractionTiles_'); }, hidden: [], disabled: [] },
    { match: function (id) { return id.startsWith('patternShapes_'); }, hidden: ['edit'], disabled: [] },
    {
        match: function (id) { return id.startsWith('counter_') || id.startsWith('numberFrameCounter_'); },
        hidden: [],
        disabled: ['edit'],
    },
    { match: function (id) { return id.startsWith('numberFrame_'); }, hidden: ['edit', 'group'], disabled: [] },
    { match: function (id) { return id.startsWith('coloredTiles_'); }, hidden: ['edit'], disabled: [] },
    {
        match: function (id) { return id.startsWith('manipulativeGroup_'); },
        hidden: ['edit'],
        disabled: [],
    },
    {
        match: function (id) { return id.startsWith('numberPiece_') || id.startsWith('moneyPiece_'); },
        hidden: [],
        disabled: [],
    },
    { match: function (id) { return id.startsWith('numberLine_'); }, hidden: ['group'], disabled: [] },
    // JumpBar: scale isn't meaningful for a bar tied to the line's units, and
    // the bar's own length is governed by the resize handles / length lock —
    // not the manipulative-scale knob shared by free-floating widgets.
    { match: function (id) { return id.startsWith('jumpBar_'); }, hidden: ['group', 'edit', 'scale'], disabled: [] },
    // CustomTick: a tick is always one unit wide on the line, so scale is
    // hidden. Up/Down move makes no geometric sense (ticks live on a 1-D
    // line) so 'moveVertical' is hidden too — the toolbox builder treats
    // this as: skip moveUp + moveDown when the rule lists it.
    {
        match: function (id) { return id.startsWith('customTick_'); },
        hidden: ['group', 'edit', 'scale', 'moveVertical', 'lock', 'duplicate'],
        disabled: [],
    },
    // LinearPiece has no configuration popup — hide the Edit / Configure entry.
    { match: function (id) { return id.startsWith('linearPiece_'); }, hidden: ['edit'], disabled: [] },
];
/**
 * Returns which tools should be hidden or disabled for a given manipulative ID.
 */
export var getToolRulesForId = function (manipId) {
    for (var _i = 0, CONTEXT_MENU_TOOL_RULES_1 = CONTEXT_MENU_TOOL_RULES; _i < CONTEXT_MENU_TOOL_RULES_1.length; _i++) {
        var rule = CONTEXT_MENU_TOOL_RULES_1[_i];
        if (rule.match(manipId)) {
            return { hiddenTools: rule.hidden, disabledTools: rule.disabled };
        }
    }
    return { hiddenTools: [], disabledTools: [] };
};
// Manipulative types that have separate lock sub-options (position, editing, length)
var SUB_LOCK_PREFIXES = [
    'arrayModel_',
    'numberLine_',
    'jumpBar_',
    'coloredTiles_',
    'tableText_',
    'numberFrame_',
];
/**
 * Returns whether a manipulative should show lock sub-menu or a simple toggle.
 */
export var hasSubLockMenu = function (manipId) {
    return SUB_LOCK_PREFIXES.some(function (prefix) { return manipId.startsWith(prefix); });
};
/**
 * Returns whether the jump bar length lock sub-option should be shown.
 */
export var hasLengthLock = function (manipId) { return manipId.startsWith('jumpBar_'); };
/**
 * Returns which buttons should be disabled based on lock state.
 * Extracted from GameController sidebar disable logic — single source of truth.
 */
export var getDisabledButtonsByLock = function (_a) {
    var _b, _c;
    var selectedTool = _a.selectedTool, text = _a.text, table = _a.table, annotations = _a.annotations, shades = _a.shades, manipulative = _a.manipulative;
    var disabled = [];
    var isEditingLocked = false;
    selectedTool.forEach(function (toolId) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
        if (toolId.startsWith('text_')) {
            var index = Number(toolId.split('_')[1]);
            if ((_c = (_b = (_a = text[index]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing) {
                isEditingLocked = true;
            }
        }
        else if (toolId.startsWith('tableText_')) {
            var parts = toolId.split('_');
            var tableIndex = Number(parts[1]);
            var tableItem = table[tableIndex];
            if (tableItem) {
                // If position is locked, disable delete
                if ((_d = tableItem.data.lock) === null || _d === void 0 ? void 0 : _d.positioning) {
                    isEditingLocked = true;
                }
                // If cell select mode is active, disable delete
                if (tableItem.data.cellSelectMode) {
                    isEditingLocked = true;
                }
                // If any cell in the table has a content lock, disable delete
                if ((_e = tableItem.data.cellData) === null || _e === void 0 ? void 0 : _e.some(function (c) { var _a, _b; return (_b = (_a = c === null || c === void 0 ? void 0 : c.data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.editing; })) {
                    isEditingLocked = true;
                }
                // Check specific cell lock if cell-level selection
                if (parts.length >= 4) {
                    var rowIndex = Number(parts[2]);
                    var colIndex = Number(parts[3]);
                    var cols = tableItem.data.colWidth.length;
                    var cellIndex = rowIndex * cols + colIndex;
                    var cell = tableItem.data.cellData[cellIndex];
                    if ((_g = (_f = cell === null || cell === void 0 ? void 0 : cell.data) === null || _f === void 0 ? void 0 : _f.lock) === null || _g === void 0 ? void 0 : _g.editing) {
                        isEditingLocked = true;
                    }
                }
            }
        }
        else if (toolId.startsWith('annotations_')) {
            var index = Number(toolId.split('_')[1]);
            if ((_k = (_j = (_h = annotations[index]) === null || _h === void 0 ? void 0 : _h.data) === null || _j === void 0 ? void 0 : _j.lock) === null || _k === void 0 ? void 0 : _k.editing) {
                isEditingLocked = true;
            }
        }
        else if (toolId.startsWith('shades_')) {
            // const index = Number(toolId.split('_')[1]);
            // if (shades[index]?.data?.lock?.editing) {
            //   isEditingLocked = true;
            // }
            var parts = toolId.split('_');
            var index = Number(parts[parts.length - 1]);
            var shadeLock = (_m = (_l = shades[index]) === null || _l === void 0 ? void 0 : _l.data) === null || _m === void 0 ? void 0 : _m.lock;
            if ((shadeLock === null || shadeLock === void 0 ? void 0 : shadeLock.editing) || (shadeLock === null || shadeLock === void 0 ? void 0 : shadeLock.positioning)) {
                isEditingLocked = true;
            }
        }
        else if (toolId.startsWith('dice_') ||
            toolId.startsWith('deck_') ||
            toolId.startsWith('spinner') ||
            toolId.startsWith('analog') ||
            toolId.startsWith('timer') ||
            toolId.startsWith('numberChart') ||
            toolId.startsWith('numberPiece_') ||
            toolId.startsWith('moneyPiece_') ||
            toolId.startsWith('coloredTiles_') ||
            toolId.startsWith('arrayModel_') ||
            toolId.startsWith('fractionTiles_') ||
            toolId.startsWith('counter_') ||
            toolId.startsWith('numberFrameCounter_') ||
            toolId.startsWith('numberFrame_') ||
            toolId.startsWith('numberRacks_') ||
            toolId.startsWith('protractor_') ||
            toolId.startsWith('linkingCube_') ||
            toolId.startsWith('patternShapes_') ||
            toolId.startsWith('coinsAndCounters_')) {
            var item = manipulative.find(function (m) { return m.data.id === toolId; });
            if (((_p = (_o = item === null || item === void 0 ? void 0 : item.data) === null || _o === void 0 ? void 0 : _o.lock) === null || _p === void 0 ? void 0 : _p.editing) || ((_r = (_q = item === null || item === void 0 ? void 0 : item.data) === null || _q === void 0 ? void 0 : _q.lock) === null || _r === void 0 ? void 0 : _r.positioning)) {
                isEditingLocked = true;
            }
        }
        else if (toolId.startsWith('card_')) {
            for (var _i = 0, manipulative_2 = manipulative; _i < manipulative_2.length; _i++) {
                var m = manipulative_2[_i];
                if (((_s = m.data) === null || _s === void 0 ? void 0 : _s.type) === 'deck') {
                    var card = (_t = m.data.cards) === null || _t === void 0 ? void 0 : _t.find(function (c) { return c.id === toolId; });
                    if ((_u = card === null || card === void 0 ? void 0 : card.lock) === null || _u === void 0 ? void 0 : _u.editing) {
                        isEditingLocked = true;
                        break;
                    }
                }
            }
        }
        else if (toolId.startsWith('manipulativeGroup_')) {
            var itemsInGroup = manipulative.filter(function (m) { return m.data.groupId === toolId; });
            if (itemsInGroup.length > 0 && itemsInGroup.every(function (m) { var _a; return (_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.positioning; })) {
                isEditingLocked = true;
            }
        }
        else if (toolId.startsWith('numberLine_') || toolId.startsWith('jumpBar_')) {
            var item = manipulative.find(function (m) { return m.data.id === toolId; });
            var lock = (_v = item === null || item === void 0 ? void 0 : item.data) === null || _v === void 0 ? void 0 : _v.lock;
            if ((lock === null || lock === void 0 ? void 0 : lock.positioning) || (lock === null || lock === void 0 ? void 0 : lock.editing) || (lock === null || lock === void 0 ? void 0 : lock.length)) {
                isEditingLocked = true;
            }
            if (toolId.startsWith('numberLine_')) {
                var childBars = manipulative.filter(function (m) { return m.data.numberLineId === toolId; });
                if (childBars.some(function (m) { var _a; return (_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.length; })) {
                    isEditingLocked = true;
                }
            }
        }
    });
    // Cards can only be deleted when every card's parent deck is also selected
    var selectedCards = selectedTool.filter(function (id) { return id.startsWith('card_'); });
    var selectedDecks = selectedTool.filter(function (id) { return id.startsWith('deck_'); });
    var cardDeleteDisabled = false;
    if (selectedCards.length > 0) {
        if (selectedDecks.length === 0) {
            cardDeleteDisabled = true;
        }
        else {
            // Find which deck actually contains each selected card (not card.deckId,
            // which can be stale after duplication)
            var cardDeckIds = new Set();
            for (var _i = 0, manipulative_1 = manipulative; _i < manipulative_1.length; _i++) {
                var m = manipulative_1[_i];
                if (((_b = m.data) === null || _b === void 0 ? void 0 : _b.type) === 'deck' && ((_c = m.data) === null || _c === void 0 ? void 0 : _c.cards)) {
                    for (var _d = 0, _e = m.data.cards; _d < _e.length; _d++) {
                        var card = _e[_d];
                        if (selectedCards.includes(card.id)) {
                            cardDeckIds.add(m.data.id);
                        }
                    }
                }
            }
            var allParentDecksSelected = Array.from(cardDeckIds).every(function (deckId) {
                return selectedDecks.includes(deckId);
            });
            if (!allParentDecksSelected) {
                cardDeleteDisabled = true;
            }
        }
    }
    var anyPreset = selectedTool.some(function (toolId) {
        var _a, _b;
        if (toolId.startsWith('card_')) {
            // Cards are nested inside deck data — check if the parent deck is preset
            return manipulative.some(function (m) {
                var _a, _b, _c, _d;
                return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'deck' &&
                    ((_b = m.data) === null || _b === void 0 ? void 0 : _b.isPreset) === true &&
                    ((_d = (_c = m.data) === null || _c === void 0 ? void 0 : _c.cards) === null || _d === void 0 ? void 0 : _d.some(function (c) { return c.id === toolId; }));
            });
        }
        if (toolId.startsWith('manipulativeGroup_')) {
            var groupMember = manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.groupId) === toolId; });
            return ((_a = groupMember === null || groupMember === void 0 ? void 0 : groupMember.data) === null || _a === void 0 ? void 0 : _a.isPreset) === true;
        }
        var item = manipulative.find(function (m) { return m.data.id === toolId; });
        return ((_b = item === null || item === void 0 ? void 0 : item.data) === null || _b === void 0 ? void 0 : _b.isPreset) === true;
    });
    if (selectedTool.length === 0 || isEditingLocked || cardDeleteDisabled || anyPreset) {
        disabled.push('delete');
    }
    if (anyPreset) {
        disabled.push('edit');
    }
    return disabled;
};
